<script setup lang="ts">
import {
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
} from 'vue';
import ConfirmDialog from 'primevue/confirmdialog';
import type { Ref } from 'vue';
import { useOpportunities, useOpportunityStatusSync, useThrottle } from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import { useSidebarStore } from '@apparatix/stores/useSidebarStore';
import OpportunitiesFilterContainer from '@apparatix/leads/OpportunitiesFilterContainer.vue';
import Header from '@apparatix/leads/Header.vue';
import LeadsTable from '@apparatix/leads/LeadsTable.vue';
import Swimlanes from '@apparatix/leads/Swimlanes.vue';
import SwimlaneSkeleton from '@apparatix/leads/SwimlaneSkeleton.vue';
import type { OpportunityType } from '@apparatix/types/Leads';
import HeightOptimizer from '@apparatix/library/HeightOptimizer.vue';
import AppContainer from '@apparatix/library/AppContainer.vue';

const props = defineProps<{ type: OpportunityType }>();

const store = useOpportunityStore();
const helper = useOpportunities();
const sidebar = useSidebarStore();

helper.loadInitialState();

const laneWidth = ref('');
const laneGap = ref('');

provide<[Ref<string>, Ref<string>]>('swimlane-sizes', [laneWidth, laneGap]);

const containerRef = ref<HTMLDivElement>();

watch(containerRef, newVal => calculateLaneSizes(newVal));

watch(() => store.selectedVisibleLanes[props.type], (newVal, oldVal) => {
  if (newVal?.length === oldVal?.length) {
    return;
  }

  calculateLaneSizes(containerRef.value);
});

watch(() => sidebar.isOpen, () => {
  if (sidebar.type === 'dynamic') return;
  calculateLaneSizes(containerRef.value);
});

const calculateLaneSizes = (container: HTMLDivElement) => {
  const [width, gap] = helper.calculateLaneWidth(container, store.selectedVisibleLanes[props.type].length);

  if (laneWidth.value !== width) {
    laneWidth.value = width;
  }

  if (laneGap.value !== gap) {
    laneGap.value = gap;
  }
};

const tableRef = ref();

const onExportRequest = () => {
  tableRef.value?.exportCsv();
};

const onWindowResize = () => {
  calculateLaneSizes(containerRef.value);
};

const throttledResizeHandler = useThrottle(onWindowResize, 250);

onMounted(() => window.addEventListener('resize', throttledResizeHandler));
onUnmounted(() => window.removeEventListener('resize', throttledResizeHandler));

const onGlobalSearchChange = (value: string) => helper.runGlobalSearch(props.type, value);

useOpportunityStatusSync(props.type === 'Opportunity');
</script>
<template>
  <AppContainer>
    <SwimlaneSkeleton v-if="!store.stateReady[props.type]" />
    <template v-if="store.stateReady[props.type]">
      <div ref="containerRef" class="flex flex-col w-full h-full gap-6 taco-burrito">
        <div class="flex-none">
          <Header
            :total-value="store.totalOpportunityValue[props.type]"
            :type="props.type"
            :existing-global-search-query="store.lastGlobalSearchQuery[props.type]"
            @update:global-search-value="onGlobalSearchChange"
            @export:data-table="onExportRequest"
          />
        </div>
        <div class="flex-none">
          <OpportunitiesFilterContainer :type="props.type" :current-view="store.viewFormat[props.type]" />
        </div>
        <HeightOptimizer class="flex-1 overflow-y-auto">
          <template #default="{availableHeight}">
            <Swimlanes
              v-if="store.viewFormat[props.type] === 'swimlanes'"
              :type="props.type"
              :opportunities="store.opportunities[props.type]"
              :visible-swimlanes="store.visibleSwimlanes[props.type]"
              :all-swimlanes="store.swimlanes[props.type]"
            />
            <LeadsTable
              v-else
              ref="tableRef"
              :type="props.type"
              :opportunities="store.opportunities[props.type]"
              :table-height="availableHeight"
            />
          </template>
        </HeightOptimizer>
      </div>
      <RouterView />
      <ConfirmDialog group="defer-campaign" />
    </template>
  </AppContainer>
</template>
